import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { isFuture } from 'date-fns';
import { setUserProperties } from '../utils/analytics.ts';

export interface AuthContextProps {
  isInitialized: boolean;
  accessToken?: string;
  refreshToken?: string;
  userId?: string;
  setTokens: (accessToken?: string, refreshToken?: string) => void;
  isLoggedIn: boolean;
  role?: string;
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [accessToken, setAccessToken] = useState<string>();
  const [refreshToken, setRefreshToken] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [role, setRole] = useState<string>();

  useEffect(() => {
    // load refresh token from local storage
    const accessTokenFromStorage = localStorage.getItem('accessToken');
    if (accessTokenFromStorage) {
      setAccessToken(accessTokenFromStorage);
    }
    const refreshTokenFromStorage = localStorage.getItem('refreshToken');
    if (refreshTokenFromStorage) {
      setRefreshToken(refreshTokenFromStorage);
    }
    if (!accessTokenFromStorage && !refreshTokenFromStorage) {
      setIsInitialized(true);
    }
    const userIdFromStorage = localStorage.getItem('userId');
    if (userIdFromStorage) {
      setUserId(userIdFromStorage);
    }
  }, []);

  useEffect(() => {
    if (accessToken && refreshToken) {
      const decodedAccessToken = jwtDecode(accessToken);
      const decodedRefreshToken = jwtDecode(refreshToken);
      if (decodedAccessToken) {
        setUserId((decodedAccessToken as any).userId);
        setRole((decodedAccessToken as any).role);
        localStorage.setItem('userId', (decodedAccessToken as any).userId);
        setUserId((decodedAccessToken as any).userId);
        const studyId = (decodedAccessToken as any).studyId;
        if (studyId) {
          setUserProperties({ study_id: studyId });
        }
      }
      if (decodedAccessToken && decodedAccessToken.exp && decodedRefreshToken && decodedRefreshToken.exp) {
        if (isFuture(new Date(decodedAccessToken.exp * 1000)) && isFuture(new Date(decodedRefreshToken.exp * 1000))) {
          setIsInitialized(true);
        } else {
          setIsInitialized(true);
        }
      }
    }
  }, [accessToken, refreshToken]);

  const setTokens = (accessToken?: string, refreshToken?: string) => {
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
      setAccessToken(accessToken);
    } else {
      localStorage.removeItem('accessToken');
      setAccessToken(undefined);
    }
    if (refreshToken) {
      setRefreshToken(refreshToken);
      localStorage.setItem('refreshToken', refreshToken);
    } else {
      localStorage.removeItem('refreshToken');
      setRefreshToken(undefined);
    }
  };

  const isLoggedIn = !!accessToken;

  return (
    <AuthContext.Provider
      value={{
        isInitialized,
        accessToken,
        refreshToken,
        setTokens,
        isLoggedIn,
        userId,
        role
      }}
      {...props}
    />
  );
};

export const useAuth = () => useContext(AuthContext);
